<template>
  <div ref="wrapper" class="reminders-quick-wrapper" v-if="visible">
    <AppHeaderDropdown right no-caret class="reminders-quick-dropdown">
      <template slot="header">
        <div class="reminders-quick-trigger"
             :class="{ 'reminders-quick-trigger--selection': hasSelectedText }"
             @mousedown="freezeSelection"
             @click="prepareForm">
          <AppIcon
            :icon="hasSelectedText ? 'alarm-fill' : 'alarm'"
            font-scale="1.25"
            weight="450"
          ></AppIcon>
          <span class="reminders-quick-selection-indicator" v-if="hasSelectedText">
            <AppIcon icon="check" aria-hidden="true"></AppIcon>
          </span>
        </div>
      </template>
      <template slot="dropdown">
        <div @click.stop class="reminders-quick-panel">
          <div class="reminders-quick-header">
            <span class="reminders-quick-title">Recordatorio rápido</span>
            <a href="#" class="reminders-quick-viewall" @click.stop.prevent="goToModule">
              Ver todos
            </a>
          </div>

          <div class="reminders-quick-body">
            <b-form-input type="text"
                          size="sm"
                          class="mb-2"
                          v-model="form.title"
                          placeholder="Qué tenés que recordar"
                          autocomplete="off">
            </b-form-input>

            <app-textarea size="sm"
                             class="mb-2"
                             v-model="form.description"
                             rows="2"
                             placeholder="Detalle opcional">
            </app-textarea>

            <b-row no-gutters class="mb-2">
              <b-col cols="7" class="pr-1">
                <b-form-input type="datetime-local"
                              size="sm"
                              v-model="form.remind_at">
                </b-form-input>
              </b-col>
              <b-col cols="5">
                <b-form-select v-model="form.notify_before_minutes" size="sm" :options="notifyBeforeOptions"></b-form-select>
              </b-col>
            </b-row>

            <div class="reminders-quick-origin text-muted" v-if="form.origin_label">
              <AppIcon icon="link-45deg"></AppIcon> {{ form.origin_label }}
            </div>

            <b-button variant="dark" size="sm" block :disabled="saving" @click="save">
              <b-spinner small v-if="saving" class="mr-1"></b-spinner>
              Guardar recordatorio
            </b-button>

            <div class="reminders-quick-hint text-muted">
              El aviso te llega solo a vos. Para elegir otros destinatarios usá "Ver todos".
            </div>
          </div>
        </div>
      </template>
    </AppHeaderDropdown>
  </div>
</template>

<script>
import serviceAPI from './services'
import Session from '@/handler/session'
import { EventBus } from '@/handler/event-bus'
import Profiles from '@/config/profiles'
import moment from 'moment'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'

export default {
  name: 'RemindersQuickAdd',
  components: {
    AppHeaderDropdown,
  },
  data: () => {
    return {
      visible: false,
      saving: false,
      selectedText: '',
      selectionSnapshot: '',
      form: {
        title: '',
        description: '',
        remind_at: '',
        notify_before_minutes: 60,
        origin_url: '',
        origin_label: '',
      },
      notifyBeforeOptions: [
        { value: 5, text: '5 min antes' },
        { value: 10, text: '10 min antes' },
        { value: 15, text: '15 min antes' },
        { value: 30, text: '30 min antes' },
        { value: 60, text: '1 hora antes' },
        { value: 120, text: '2 horas antes' },
        { value: 1440, text: '1 día antes' },
      ],
    }
  },
  created () {
    EventBus.$on('Auth:auth', () => {
      this.setup()
    })
  },
  mounted() {
    this.setup()
    document.addEventListener('selectionchange', this.updateSelectedText)
  },
  beforeDestroy() {
    document.removeEventListener('selectionchange', this.updateSelectedText)
  },
  computed: {
    hasSelectedText() {
      return Boolean(this.selectedText)
    },
  },
  methods: {
    setup () {
      var session = Session.getSession()

      // Solo staff con acceso al módulo de recordatorios en su menú.
      this.visible = session.auth &&
                     session.auth.user.profile_id == Profiles.PERSONAL &&
                     this.hasModuleAccess(session)
    },
    hasModuleAccess(session) {
      var menu = session.auth.user.permissions && session.auth.user.permissions.menu
      if (!menu) return false

      for (const prop in menu) {
        if (menu[prop].router == 'reminders') return true
      }
      return false
    },

    readSelectedText() {
      if (!window.getSelection) return ''

      var selection = window.getSelection()
      if (!selection || selection.isCollapsed) return ''

      var anchor = selection.anchorNode
      var anchorElement = anchor && (anchor.nodeType === 1 ? anchor : anchor.parentElement)
      if (anchorElement && this.$refs.wrapper && this.$refs.wrapper.contains(anchorElement)) return ''

      return selection.toString().trim().substring(0, 500)
    },
    updateSelectedText() {
      this.selectedText = this.readSelectedText()
    },
    freezeSelection() {
      this.selectionSnapshot = this.readSelectedText() || this.selectedText
    },

    // La "magia" de la captura rápida: al abrir la burbuja se precarga el
    // texto seleccionado en pantalla como descripción, y la vista actual
    // queda como origen para poder volver con un click desde el recordatorio.
    prepareForm() {
      var selection = this.readSelectedText() || this.selectionSnapshot
      if (selection && !this.form.description) {
        this.form.description = selection
      }
      this.selectionSnapshot = ''

      if (!this.form.remind_at) {
        this.form.remind_at = moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
      }

      this.form.origin_url = this.$route.fullPath
      this.form.origin_label = this.$route.meta && this.$route.meta.title ? this.$route.meta.title : this.$route.fullPath
    },
    goToModule() {
      document.body.click()
      if (this.$route.name != 'RemindersStaffCrud') {
        this.$router.push({ name: 'RemindersStaffCrud' })
      }
    },
    save() {
      if (!this.form.title.trim()) {
        this.$awn.alert("El título es obligatorio")
        return
      }
      if (!this.form.remind_at) {
        this.$awn.alert("La fecha y hora del recordatorio es obligatoria")
        return
      }

      this.saving = true

      serviceAPI.createReminder({
        title: this.form.title,
        description: this.form.description,
        remind_at: this.form.remind_at,
        notify_before_minutes: this.form.notify_before_minutes,
        origin_url: this.form.origin_url,
        origin_label: this.form.origin_label,
      })
      .then(() => {
        this.saving = false
        this.$awn.success("Recordatorio guardado")
        this.resetForm()
        document.body.click()
      })
      .catch(() => {
        this.saving = false
        this.$awn.alert("No se pudo guardar el recordatorio")
      })
    },
    resetForm() {
      this.form.title = ''
      this.form.description = ''
      this.form.remind_at = ''
      this.form.notify_before_minutes = 60
      this.form.origin_url = ''
      this.form.origin_label = ''
    },
  },
}
</script>

<style scoped>
.reminders-quick-trigger {
  position: relative;
  width: 34px;
  height: 34px;
  margin: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--app-color-icon);
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.reminders-quick-trigger:hover {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.reminders-quick-trigger--selection {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  box-shadow: 0 0 0 2px var(--app-color-surface), var(--app-focus-ring);
  transform: translateY(-1px);
  animation: reminders-selection-pulse 1.35s ease-in-out infinite;
}
.reminders-quick-trigger--selection:hover {
  background-color: var(--app-color-primary-soft-hover);
  color: var(--app-color-primary-active);
}
.reminders-quick-selection-indicator {
  position: absolute;
  top: -3px;
  right: -3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border: 2px solid var(--app-color-surface);
  border-radius: 50%;
  background-color: var(--app-color-success);
  color: var(--app-color-on-primary);
  font-size: 10px;
  line-height: 1;
  animation: reminders-selection-glint 1.35s ease-in-out infinite;
}
@keyframes reminders-selection-pulse {
  0%, 100% {
    background-color: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    box-shadow: 0 0 0 2px var(--app-color-surface), var(--app-focus-ring);
  }
  50% {
    background-color: var(--app-color-primary-soft-hover);
    color: var(--app-color-primary-active);
    box-shadow: 0 0 0 2px var(--app-color-surface), 0 0 0 7px rgba(0, 111, 140, 0.14), 0 0 14px rgba(0, 111, 140, 0.32);
  }
}
@keyframes reminders-selection-glint {
  0%, 60%, 100% {
    filter: brightness(1);
    transform: scale(1);
  }
  78% {
    filter: brightness(1.45);
    transform: scale(1.18);
  }
}
@media (prefers-reduced-motion: reduce) {
  .reminders-quick-trigger--selection,
  .reminders-quick-selection-indicator {
    animation: none;
  }
}
</style>

<style>
.reminders-quick-dropdown .dropdown-menu {
  /* right:auto — ver comentario igual en NotificationsBell.vue: sin esto,
     left (Popper) + right:0 (.dropdown-menu-right) compiten y el panel
     puede terminar pegado al borde izquierdo según el ancho del viewport. */
  right: auto;
  min-width: 320px;
  width: 320px;
  padding: 0;
  overflow: hidden;
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
}
/* El panel se alinea (correctamente) por su borde derecho contra el propio
   ícono que lo abre — pero como es mucho más ancho que el ícono y en
   mobile suele haber más íconos a la derecha del que se tocó, se estira
   mucho hacia la izquierda y visualmente "flota" desconectado de su
   burbuja. Esta flechita conecta ambos con el mismo truco de cualquier
   popover: no depende de la posición que calculó Popper, solo señala
   desde el ícono hacia el panel. Va afuera del .dropdown-menu (que tiene
   overflow:hidden para las esquinas redondeadas) para no quedar recortada. */
.reminders-quick-dropdown.show::after {
  content: '';
  position: absolute;
  top: 28px;
  right: 21px;
  width: 12px;
  height: 12px;
  background: var(--app-color-surface);
  transform: rotate(45deg);
  box-shadow: -1px -1px 3px rgba(11, 31, 59, 0.08);
  z-index: 1001;
  pointer-events: none;
}
.reminders-quick-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-color-border);
}
.reminders-quick-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--app-color-text);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.reminders-quick-viewall {
  font-size: 11px;
  color: var(--app-color-primary);
  white-space: nowrap;
}
.reminders-quick-viewall:hover {
  color: var(--app-color-primary-hover);
  text-decoration: underline;
}
.reminders-quick-body {
  padding: 12px;
}
.reminders-quick-origin {
  font-size: 11px;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.reminders-quick-hint {
  font-size: 11px;
  margin-top: 8px;
  line-height: 1.4;
}
</style>
